import {
  AnalysisScope,
  ValidationSeverity,
  MESSAGE_DICTIONARY_VERSION,
  type AnalysisIssue,
  type MessageAnalysisResult,
  type MessageDefinition,
  type MessageEvent,
} from '@k-ucap/shared';
import { createMessageDictionaryService } from './MessageDictionaryService.js';
import { createMessageValidationService } from './MessageValidationService.js';
import { createPreviewMessageEncoder } from './PreviewMessageEncoder.js';

const ANALYZER_VERSION = '0.1.0-poc';
const TIMING_TOLERANCE = 0.3;
const SEQUENCE_GAP_THRESHOLD = 5;

export interface MessageAnalysisOptions {
  previousEvents?: MessageEvent[];
}

export class MessageAnalysisService {
  private readonly dictionaryService = createMessageDictionaryService();
  private readonly validationService = createMessageValidationService();
  private readonly encoder = createPreviewMessageEncoder();

  analyze(event: MessageEvent, options?: MessageAnalysisOptions): MessageAnalysisResult {
    const definition = this.dictionaryService.getMessageByType(event.messageType);
    const decoded = event.rawBytes ? this.encoder.decode(new Uint8Array(event.rawBytes)) : {};

    const definitionResults = this.analyzeDefinition(event, definition);
    const headerResults = this.analyzeHeader(event, decoded);
    const fieldResults = this.analyzeFields(event, definition);
    const roleResults = this.analyzeRole(event, definition);
    const sequenceResults = this.analyzeSequence(event, options?.previousEvents ?? []);
    const timingResults = this.analyzeTiming(event, options?.previousEvents ?? [], definition);

    const allIssues = [
      ...definitionResults,
      ...headerResults,
      ...fieldResults,
      ...roleResults,
      ...sequenceResults,
      ...timingResults,
    ];

    const overallStatus = this.aggregateSeverity(allIssues);
    const summary = this.buildSummary(allIssues);

    return {
      id: crypto.randomUUID(),
      messageEventId: event.id,
      projectId: event.projectId,
      environment: event.environment,
      messageType: event.messageType,
      messageName: event.name,
      overallStatus,
      definitionStatus: this.aggregateSeverity(definitionResults),
      definitionResults,
      headerResults,
      fieldResults,
      roleResults,
      sequenceResults,
      timingResults,
      summary,
      analyzedAt: new Date().toISOString(),
      analyzerVersion: ANALYZER_VERSION,
      dictionaryVersion: definition?.dictionaryVersion ?? MESSAGE_DICTIONARY_VERSION,
      metadata: {
        encoder: this.encoder.name,
        standard: this.encoder.standard,
      },
    };
  }

  private analyzeDefinition(event: MessageEvent, definition: MessageDefinition | undefined): AnalysisIssue[] {
    const issues: AnalysisIssue[] = [];
    if (!definition) {
      issues.push({
        severity: ValidationSeverity.UNKNOWN,
        code: 'DEFINITION_NOT_FOUND',
        message: `Message Type ${event.messageType}에 대한 정의를 찾을 수 없습니다.`,
        path: 'definition',
        scope: AnalysisScope.DEFINITION,
        ruleSource: 'POC',
      });
      return issues;
    }
    issues.push({
      severity: ValidationSeverity.PASS,
      code: 'DEFINITION_FOUND',
      message: `${definition.name} 정의가 존재합니다.`,
      path: 'definition',
      scope: AnalysisScope.DEFINITION,
      expected: definition.id,
      actual: event.messageDefinitionId,
      ruleSource: 'POC',
    });

    if (!definition.supported) {
      issues.push({
        severity: ValidationSeverity.UNSUPPORTED,
        code: 'DEFINITION_UNSUPPORTED',
        message: `${definition.name}은(는) PoC에서 지원되지 않는 메시지입니다.`,
        path: 'definition',
        scope: AnalysisScope.DEFINITION,
        ruleSource: 'POC',
      });
    }

    if (event.messageDefinitionId && event.messageDefinitionId !== definition.id) {
      issues.push({
        severity: ValidationSeverity.WARN,
        code: 'DEFINITION_ID_MISMATCH',
        message: 'MessageEvent의 messageDefinitionId와 Dictionary 정의가 다릅니다.',
        path: 'definition.id',
        scope: AnalysisScope.DEFINITION,
        expected: definition.id,
        actual: event.messageDefinitionId,
        ruleSource: 'POC',
      });
    }

    if (definition.dictionaryVersion && definition.dictionaryVersion !== MESSAGE_DICTIONARY_VERSION) {
      issues.push({
        severity: ValidationSeverity.WARN,
        code: 'DICTIONARY_VERSION_MISMATCH',
        message: 'Message Dictionary 버전이 불일치합니다.',
        path: 'definition.dictionaryVersion',
        scope: AnalysisScope.DEFINITION,
        expected: MESSAGE_DICTIONARY_VERSION,
        actual: definition.dictionaryVersion,
        ruleSource: 'POC',
      });
    }

    return issues;
  }

  private analyzeHeader(event: MessageEvent, decoded: Partial<MessageEvent>): AnalysisIssue[] {
    const issues: AnalysisIssue[] = [];
    if (event.messageType === decoded.messageType) {
      issues.push({
        severity: ValidationSeverity.PASS,
        code: 'HEADER_MESSAGE_TYPE_MATCH',
        message: 'Header Message Type이 일치합니다.',
        path: 'header.messageType',
        scope: AnalysisScope.HEADER,
        expected: String(event.messageType),
        actual: String(decoded.messageType),
        ruleSource: 'POC',
      });
    } else {
      issues.push({
        severity: ValidationSeverity.FAIL,
        code: 'HEADER_MESSAGE_TYPE_MISMATCH',
        message: 'Header Message Type이 불일치합니다.',
        path: 'header.messageType',
        scope: AnalysisScope.HEADER,
        expected: String(event.messageType),
        actual: String(decoded.messageType),
        ruleSource: 'POC',
      });
    }

    if (event.sequenceNumber === decoded.sequenceNumber) {
      issues.push({
        severity: ValidationSeverity.PASS,
        code: 'HEADER_SEQUENCE_MATCH',
        message: 'Header Sequence Number가 일치합니다.',
        path: 'header.sequenceNumber',
        scope: AnalysisScope.HEADER,
        expected: String(event.sequenceNumber),
        actual: String(decoded.sequenceNumber),
        ruleSource: 'POC',
      });
    } else {
      issues.push({
        severity: ValidationSeverity.WARN,
        code: 'HEADER_SEQUENCE_MISMATCH',
        message: 'Header Sequence Number가 불일치합니다.',
        path: 'header.sequenceNumber',
        scope: AnalysisScope.HEADER,
        expected: String(event.sequenceNumber),
        actual: String(decoded.sequenceNumber),
        ruleSource: 'POC',
      });
    }

    issues.push({
      severity: ValidationSeverity.INFO,
      code: 'HEADER_ENCODING',
      message: `PoC Preview Encoder 기준 Header: ${this.encoder.name} / ${this.encoder.standard}`,
      path: 'header.encoding',
      scope: AnalysisScope.ENCODING,
      expected: 'PoC Preview Encoder',
      actual: this.encoder.name,
      ruleSource: 'POC',
    });

    return issues;
  }

  private analyzeFields(event: MessageEvent, definition: MessageDefinition | undefined): AnalysisIssue[] {
    if (!definition) return [];
    const validationResult = this.validationService.validate(definition, event.payload ?? event.fields ?? {});
    return validationResult.issues.map((issue) => ({
      ...issue,
      scope: (issue.scope as AnalysisScope) ?? AnalysisScope.FIELD,
      ruleSource: (issue as AnalysisIssue).ruleSource ?? 'POC',
    }));
  }

  private analyzeRole(event: MessageEvent, definition: MessageDefinition | undefined): AnalysisIssue[] {
    const issues: AnalysisIssue[] = [];
    const expectedSource = definition?.sourceRole ?? event.sourceRole;
    const expectedTarget = definition?.targetRole ?? event.targetRole;

    const checkRole = (label: string, expected: string | undefined, actual: string | undefined): void => {
      if (!expected || expected === 'ANY') {
        issues.push({
          severity: ValidationSeverity.PASS,
          code: `ROLE_${label.toUpperCase()}_ANY`,
          message: `${label} Role이 ANY로 허용됩니다.`,
          path: `role.${label}`,
          scope: AnalysisScope.ROLE,
          expected,
          actual,
          ruleSource: 'POC',
        });
      } else if (expected === actual) {
        issues.push({
          severity: ValidationSeverity.PASS,
          code: `ROLE_${label.toUpperCase()}_MATCH`,
          message: `${label} Role이 일치합니다.`,
          path: `role.${label}`,
          scope: AnalysisScope.ROLE,
          expected,
          actual,
          ruleSource: 'POC',
        });
      } else {
        issues.push({
          severity: ValidationSeverity.FAIL,
          code: `ROLE_${label.toUpperCase()}_MISMATCH`,
          message: `${label} Role이 불일치합니다.`,
          path: `role.${label}`,
          scope: AnalysisScope.ROLE,
          expected,
          actual,
          ruleSource: 'POC',
        });
      }
    };

    checkRole('source', expectedSource, event.sourceRole);
    checkRole('target', expectedTarget, event.targetRole);

    return issues;
  }

  private analyzeSequence(event: MessageEvent, previousEvents: MessageEvent[]): AnalysisIssue[] {
    const issues: AnalysisIssue[] = [];
    if (event.sequenceNumber === undefined || event.sequenceNumber === null) {
      issues.push({
        severity: ValidationSeverity.WARN,
        code: 'SEQUENCE_MISSING',
        message: 'Sequence Number가 없습니다.',
        path: 'sequence',
        scope: AnalysisScope.SEQUENCE,
        ruleSource: 'POC',
      });
      return issues;
    }

    const sameTypeEvents = previousEvents
      .filter((e) => e.messageType === event.messageType && e.projectId === event.projectId)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    if (sameTypeEvents.length === 0) {
      issues.push({
        severity: ValidationSeverity.INFO,
        code: 'SEQUENCE_FIRST',
        message: '해당 Message Type의 첫 번째 메시지입니다.',
        path: 'sequence',
        scope: AnalysisScope.SEQUENCE,
        expected: 'N/A',
        actual: String(event.sequenceNumber),
        ruleSource: 'POC',
      });
      return issues;
    }

    const prev = sameTypeEvents[0];
    const prevSeq = prev.sequenceNumber ?? 0;
    const currSeq = event.sequenceNumber;

    if (currSeq === prevSeq) {
      issues.push({
        severity: ValidationSeverity.WARN,
        code: 'SEQUENCE_DUPLICATE',
        message: 'Sequence Number가 중복되었습니다.',
        path: 'sequence',
        scope: AnalysisScope.SEQUENCE,
        expected: String(prevSeq + 1),
        actual: String(currSeq),
        ruleSource: 'POC',
      });
    } else if (currSeq < prevSeq) {
      issues.push({
        severity: ValidationSeverity.WARN,
        code: 'SEQUENCE_REVERSED',
        message: 'Sequence Number가 감소했습니다.',
        path: 'sequence',
        scope: AnalysisScope.SEQUENCE,
        expected: `> ${prevSeq}`,
        actual: String(currSeq),
        ruleSource: 'POC',
      });
    } else if (currSeq - prevSeq > SEQUENCE_GAP_THRESHOLD) {
      issues.push({
        severity: ValidationSeverity.WARN,
        code: 'SEQUENCE_GAP',
        message: `Sequence Number Gap이 큽니다. (${currSeq - prevSeq})`,
        path: 'sequence',
        scope: AnalysisScope.SEQUENCE,
        expected: String(prevSeq + 1),
        actual: String(currSeq),
        ruleSource: 'POC',
      });
    } else {
      issues.push({
        severity: ValidationSeverity.PASS,
        code: 'SEQUENCE_NORMAL',
        message: 'Sequence Number가 정상적으로 증가했습니다.',
        path: 'sequence',
        scope: AnalysisScope.SEQUENCE,
        expected: String(prevSeq + 1),
        actual: String(currSeq),
        ruleSource: 'POC',
      });
    }

    return issues;
  }

  private analyzeTiming(
    event: MessageEvent,
    previousEvents: MessageEvent[],
    definition: MessageDefinition | undefined
  ): AnalysisIssue[] {
    const issues: AnalysisIssue[] = [];
    if (!definition?.defaultPeriodMs) {
      issues.push({
        severity: ValidationSeverity.UNKNOWN,
        code: 'TIMING_NO_RULE',
        message: 'Message Definition에 주기 규칙이 없습니다.',
        path: 'timing',
        scope: AnalysisScope.TIMING,
        ruleSource: 'POC',
      });
      return issues;
    }

    const sameTypeEvents = previousEvents
      .filter((e) => e.messageType === event.messageType && e.projectId === event.projectId)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    if (sameTypeEvents.length === 0) {
      issues.push({
        severity: ValidationSeverity.INFO,
        code: 'TIMING_FIRST',
        message: '해당 Message Type의 첫 번째 메시지입니다.',
        path: 'timing',
        scope: AnalysisScope.TIMING,
        expected: `${definition.defaultPeriodMs}ms`,
        actual: 'N/A',
        ruleSource: 'POC',
      });
      return issues;
    }

    const prev = sameTypeEvents[0];
    const prevTime = new Date(prev.timestamp).getTime();
    const currTime = new Date(event.timestamp).getTime();
    const actualMs = currTime - prevTime;
    const expectedMs = definition.defaultPeriodMs;
    const lower = expectedMs * (1 - TIMING_TOLERANCE);
    const upper = expectedMs * (1 + TIMING_TOLERANCE);

    if (actualMs < 0) {
      issues.push({
        severity: ValidationSeverity.WARN,
        code: 'TIMING_REVERSED',
        message: 'Timestamp가 역순입니다.',
        path: 'timing',
        scope: AnalysisScope.TIMING,
        expected: `${expectedMs}ms ±${TIMING_TOLERANCE * 100}%`,
        actual: `${actualMs}ms`,
        ruleSource: 'POC',
      });
    } else if (actualMs >= lower && actualMs <= upper) {
      issues.push({
        severity: ValidationSeverity.PASS,
        code: 'TIMING_PASS',
        message: '주기가 허용 범위 내입니다.',
        path: 'timing',
        scope: AnalysisScope.TIMING,
        expected: `${expectedMs}ms ±${TIMING_TOLERANCE * 100}%`,
        actual: `${actualMs}ms`,
        ruleSource: 'POC',
      });
    } else if (actualMs > upper && actualMs <= upper * 2) {
      issues.push({
        severity: ValidationSeverity.WARN,
        code: 'TIMING_SLOW',
        message: '주기가 허용 범위를 초과했습니다.',
        path: 'timing',
        scope: AnalysisScope.TIMING,
        expected: `${expectedMs}ms ±${TIMING_TOLERANCE * 100}%`,
        actual: `${actualMs}ms`,
        ruleSource: 'POC',
      });
    } else {
      issues.push({
        severity: ValidationSeverity.FAIL,
        code: 'TIMING_FAIL',
        message: '주기가 크게 벗어났습니다.',
        path: 'timing',
        scope: AnalysisScope.TIMING,
        expected: `${expectedMs}ms ±${TIMING_TOLERANCE * 100}%`,
        actual: `${actualMs}ms`,
        ruleSource: 'POC',
      });
    }

    return issues;
  }

  private aggregateSeverity(issues: AnalysisIssue[]): ValidationSeverity {
    if (issues.some((i) => i.severity === ValidationSeverity.FAIL)) return ValidationSeverity.FAIL;
    if (issues.some((i) => i.severity === ValidationSeverity.UNSUPPORTED)) return ValidationSeverity.UNSUPPORTED;
    if (issues.some((i) => i.severity === ValidationSeverity.WARN)) return ValidationSeverity.WARN;
    if (issues.some((i) => i.severity === ValidationSeverity.UNKNOWN)) return ValidationSeverity.UNKNOWN;
    if (issues.some((i) => i.severity === ValidationSeverity.INFO)) return ValidationSeverity.INFO;
    return ValidationSeverity.PASS;
  }

  private buildSummary(issues: AnalysisIssue[]) {
    return {
      pass: issues.filter((i) => i.severity === ValidationSeverity.PASS).length,
      warn: issues.filter((i) => i.severity === ValidationSeverity.WARN).length,
      fail: issues.filter((i) => i.severity === ValidationSeverity.FAIL).length,
      unknown: issues.filter((i) => i.severity === ValidationSeverity.UNKNOWN).length,
      unsupported: issues.filter((i) => i.severity === ValidationSeverity.UNSUPPORTED).length,
    };
  }
}

export const createMessageAnalysisService = (): MessageAnalysisService => new MessageAnalysisService();
